import React, { useState, useEffect, useRef } from 'react'
import FileReaderWidget from '../../../widgets/file_reader/file_reader';
import PagesTitle from '../../../widgets/pages_title/pages_title';
import './event_view.css';
import { HideProgressAlert, showProgressAlert } from '../../../functions/show_progress_page';
import { hundleRequestFailure } from '../../../functions/hundle_request_failure';
import { changeProjectDomainAPI } from '../../../api/project';
import { useDispatch } from 'react-redux';
import { updateEventDomain } from '../../../redux/project/projects';
import { convertDay } from '../../../functions/date_converter';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { domainType, formatProblemDisplay } from '../../../global/http_enum';

export default function EventView({ event, id, onBack }) {
    const dispatch = useDispatch();
    const [mapCenter, setMapCenter] = useState({
        lat: 20.5937,
        lng: 78.9629
    });
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef(null);

    // Debug: Log event data when component mounts or event changes
    useEffect(() => {
        console.log('EventView - Event data:', event);
        console.log('EventView - Event location:', event?.location);
    }, [event]);

    // Update map when center changes after map is loaded
    useEffect(() => {
        if (mapRef.current && mapLoaded && mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629) {
            console.log('🔄 Updating map center via useEffect to:', mapCenter);
            try {
                mapRef.current.setCenter(mapCenter);
                mapRef.current.setZoom(12);
            } catch (error) {
                console.error('Error updating map center:', error);
            }
        }
    }, [mapCenter, mapLoaded]);

    // Geocode location for map center
    useEffect(() => {
        const geocodeLocation = async () => {
            if (event?.location && event.location.trim() !== '') {
                try {
                    console.log('Geocoding location in EventView:', event.location);
                    const geocodeURL = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(event.location)}&key=AIzaSyA1fQ3PPqStI2hluImIPswrqIFYcSobIZI`;
                    const response = await fetch(geocodeURL);
                    const data = await response.json();

                    console.log('Geocoding response in EventView:', data);

                    if (data.status === 'OK' && data.results && data.results.length > 0) {
                        const location = data.results[0].geometry.location;
                        const newCenter = {
                            lat: location.lat,
                            lng: location.lng
                        };
                        console.log('Setting map center in EventView to:', newCenter);
                        setMapCenter(newCenter);
                    } else {
                        console.warn('Geocoding failed or no results in EventView:', data.status, data);
                        // Keep default center if geocoding fails
                    }
                } catch (error) {
                    console.error('Error geocoding location in EventView:', error);
                }
            } else {
                console.log('No location found for event in EventView, event:', event);
            }
        };

        // Only geocode if we have a valid location
        if (event?.location) {
            geocodeLocation();
        }
    }, [event]);

    const changeDomain = async (domain) => {
        if (event?.domain === domain) {
            return;
        }
        showProgressAlert();
        const response = await changeProjectDomainAPI(event.id, domain);
        HideProgressAlert();
        if (response.statusCode < 300) {
            dispatch(updateEventDomain({ projectId: id, eventId: event.id, newDomain: domain }));
        } else {
            hundleRequestFailure(response);
        }
    }

    // Format date
    const eventDate = event.date || event.created_at;
    const formattedDate = eventDate ? convertDay(eventDate) : '';

    // Determine file types - check both event.file and event.link for YouTube
    // YouTube link can be in event.link OR event.file with type 'link'
    const youtubeLinkFromFile = event.type === 'link' ? event.file : null;
    const youtubeLink = event.link || youtubeLinkFromFile;
    const isYouTubeVideo = youtubeLink && (youtubeLink.includes('youtube.com') || youtubeLink.includes('youtu.be'));

    // Uploaded file is in event.file when type is NOT 'link'
    // If event.link exists (YouTube in separate field), then event.file can be a separate uploaded file
    // If event.type === 'link', then event.file contains YouTube link, not an uploaded file
    // So uploaded file exists when: event.file exists AND type is image/video/audio
    const hasUploadedFile = event.file &&
        event.type &&
        event.type !== 'link' &&
        (event.type === 'image' || event.type === 'video' || event.type === 'audio');

    const mapContainerStyle = {
        width: '100%',
        height: '300px',
        borderRadius: '5px',
        position: 'relative'
    };

    return (
        <>
            <div id='event-view'>
                <div className="title-holder">
                    <PagesTitle title={'Event Details'} />
                    <div className="domain-pills">
                        <button
                            className={`domain-pill ${(event?.domain || domainType.theory) === domainType.theory ? 'active' : ''}`}
                            onClick={() => changeDomain(domainType.theory)}
                        >
                            Theory Domain
                        </button>
                        <button
                            className={`domain-pill ${(event?.domain || domainType.theory) === domainType.communication ? 'active' : ''}`}
                            onClick={() => changeDomain(domainType.communication)}
                        >
                            Communication Domain
                        </button>
                    </div>
                </div>

                <div className="event-details-section">
                    <div className="detail-item">
                        <h3 className="detail-label">Event Name</h3>
                        <p className="detail-value">{event.name || 'N/A'}</p>
                    </div>

                    <div className="detail-item">
                        <h3 className="detail-label">Event Location</h3>
                        <p className="detail-value">{event.location || 'N/A'}</p>
                    </div>

                    {formattedDate && (
                        <div className="detail-item">
                            <h3 className="detail-label">Event Date</h3>
                            <p className="detail-value">{formattedDate}</p>
                        </div>
                    )}

                    <div className="detail-item">
                        <h3 className="detail-label">Negative Function Executed</h3>
                        <p className="detail-value negative-function">{event.negative_function_exluded || 'N/A'}</p>
                    </div>

                    <div className="detail-item">
                        <h3 className="detail-label">Problem Identified from Event</h3>
                        <p className="detail-value problem">{formatProblemDisplay(event.problem_identifyed_from_event) || 'N/A'}</p>
                    </div>

                    <div className="detail-item description-item">
                        <h3 className="detail-label">Event Description</h3>
                        <div
                            className="detail-value description-text"
                            dangerouslySetInnerHTML={{ __html: event.desciption || 'N/A' }}
                        />
                    </div>
                </div>

                <div className="media-section">
                    <div className="media-panel">
                        <h3 className="media-title">Event Map</h3>
                        <div className="map-container" style={{ width: '100%', height: '300px', position: 'relative', backgroundColor: '#f5f5f5', border: '1px solid #ddd' }}>
                            {event?.location && event.location.trim() !== '' ? (
                                <div style={{ width: '100%', height: '300px', position: 'relative' }}>
                                    <LoadScript
                                        googleMapsApiKey="AIzaSyA1fQ3PPqStI2hluImIPswrqIFYcSobIZI"
                                        loadingElement={<div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>Loading map...</div>}
                                    >
                                        <GoogleMap
                                            key={`map-${event?.id || 'new'}-${mapCenter.lat}-${mapCenter.lng}`}
                                            mapContainerStyle={mapContainerStyle}
                                            zoom={mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629 ? 12 : 2}
                                            center={mapCenter}
                                            options={{
                                                disableDefaultUI: false,
                                                zoomControl: true,
                                                streetViewControl: false,
                                                mapTypeControl: false,
                                                fullscreenControl: false,
                                            }}
                                            onLoad={(map) => {
                                                console.log('✅ GoogleMap loaded in EventView');
                                                console.log('📍 Map center:', mapCenter);
                                                console.log('📍 Event location:', event.location);
                                                mapRef.current = map;
                                                setMapLoaded(true);
                                                // Always update map center when map loads if we have valid coordinates
                                                if (mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629) {
                                                    console.log('🔄 Setting map center on load to:', mapCenter);
                                                    setTimeout(() => {
                                                        map.setCenter(mapCenter);
                                                        map.setZoom(12);
                                                    }, 100);
                                                }
                                            }}
                                            onError={(error) => {
                                                console.error('❌ GoogleMap error in EventView:', error);
                                            }}
                                        >
                                            {mapCenter.lat !== 20.5937 && mapCenter.lng !== 78.9629 && (
                                                <Marker
                                                    position={mapCenter}
                                                    title={event.location}
                                                    onLoad={() => console.log('✅ Marker loaded at:', mapCenter)}
                                                />
                                            )}
                                        </GoogleMap>
                                    </LoadScript>
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                                    No location set for this event
                                </div>
                            )}
                        </div>
                    </div>

                    {isYouTubeVideo && (
                        <div className="media-panel">
                            <h3 className="media-title">YouTube Video</h3>
                            <div className="youtube-container">
                                <FileReaderWidget file={youtubeLink} type="link" />
                            </div>
                        </div>
                    )}

                    {hasUploadedFile && (
                        <div className="media-panel">
                            <h3 className="media-title">Files</h3>
                            <div className="file-container">
                                <FileReaderWidget file={event.file} type={event.type} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}
